import { CurrencyService } from './currency-service';
import { EventAggregator, inject, observable, watch } from 'aurelia';
import { LocationChangeEvent } from '@aurelia/router-lite/src/router-events';

import { User } from './models/user';
import { ApiService } from './api-service';
import { BlacklistService } from './blacklist-service';
import { CustomerService } from './customer-service';

import { ILoginResponse } from '@chicksgroup/types';
import { baseUrl, clientId, websiteShortCode } from '../environment';
import { TokenService } from './token-service';
import notifications from '../resources/notifications-manager';
import { LanguageWebsiteService } from './language-website-service';
import { SignalBindingBehavior } from '@aurelia/runtime-html';
import countryToCurrency from 'country-to-currency';
import { Geolocation } from './models/language/geolocation';
import { Currency } from './models';
import { countryToLanguage } from './models/language/country-to-language';

const REFERRER_USER_ID = 'refferer_user_id';
const ADMIN_VIEW = 'admin_view';
const CART_KEY = 'cart';
const CURRENT_BLOGS_PAGE_VIEW = 'current_blogs_page_view';
const WEBSITE_SHORTCODE = 'website_shortcode';
const CURRENCY_KEY = 'currency';
const PREVIOUS_CURRENCY_KEY = 'previous_currency';
const LANGUAGE_KEY = 'language';

const websiteId = new Map<string, string>([
    ['CG', '3a6a1d123b4b45a5ab4d7d097fd900df'],
    ['CX', '96d4812af5784a1a8613d1c5a91a2b0d'],
    ['AG', '5a62c4b8c36a43bca304a85e7ee0cba4'],
    ['GT', '3f8951e437494f0d902c5c0c3532eff1'],
    ['AK', '6dd27a0175454774b65566e112445a64'],
    ['DS', '7534338221d44c99bf41301a56c0ed31']
]);

@inject(
    ApiService,
    EventAggregator,
    CustomerService,
    BlacklistService,
    TokenService,
    LanguageWebsiteService,
    CurrencyService,
    SignalBindingBehavior
)
export class SessionService {
    isAuthenticated = false;
    currentUser: User | null = null;
    @observable geolocation: Geolocation;
    userProxy;
    private intercomTimer: NodeJS.Timer;
    CURRENCY_OPTIONS: any[] = [];
    userCart;
    userBlacklist;
    canOpenLiveChat = true;
    intervalAttempts;
    private geolocationPromise: Promise<void> | null = null;

    constructor(
        private apiService: ApiService,
        private eventAggregator: EventAggregator,
        private customerService: CustomerService,
        private blacklistService: BlacklistService,
        private tokenService: TokenService,
        private languageWebsiteService: LanguageWebsiteService,
        private CurrencyService: CurrencyService
    ) {}

    _currentRoute: LocationChangeEvent;
    set currentRoute(locationChangeEvent: LocationChangeEvent) {
        this._currentRoute = locationChangeEvent;
    }

    get currentRoute() {
        return this._currentRoute;
    }

    async login(email: string) {
        const response = await this.apiService.doPost<ILoginResponse>('', {
            email,
            password: '12345678',
            skipChecks: true,
            websiteShortCode: 'CG',
            token: ''
        });

        if (response) {
            if (!response.resetPassword) {
                if (response.userBlacklist?.length) {
                    this.userBlacklist = response.userBlacklist;
                    const blackListResponse = await this.verifyIsUserInBlackList(null, true);

                    if (blackListResponse) {
                        return null;
                    }
                }

                this.tokenService.saveToken(response.token);
                this.isAuthenticated = true;
            }
        }

        return response;
    }

    async getProfile() {
        if (await this.tokenService.isTokenValid()) {
            if (this.currentUser) {
                return this.currentUser;
            }

            this.currentUser = await this.apiService.doGet('profile');

            if (!this.currentUser?.isDeleted) {
                this.tokenService.saveToken(this.currentUser?.refreshToken?.token);
            } else if (this.currentUser?.isDeleted) {
                notifications.toast('As a result you have been logged out, please Sign up again to access your account or contact Support.', 'info', 'Account deleted').show();
                await this.logout();
            }

            if (this.currentUser?.userBlacklist?.length > 0) {
                this.userBlacklist = this.currentUser?.userBlacklist;
                const blackListResponse = await this.verifyIsUserInBlackList(null, true);

                if (blackListResponse) {
                    await this.logout();
                }
            }

            return this.currentUser;
        }

        return null;
    }

    async refreshProfile() {
        if (await this.tokenService.isTokenValid()) {
            this.currentUser = await this.apiService.doGet('profile');

            if (!this.currentUser?.isDeleted) {
                return this.currentUser;
            } else if (this.currentUser?.isDeleted) {
                notifications.toast('As a result you have been logged out, please Sign up again to access your account or contact Support.', 'info', 'Account deleted').show();
                await this.logout();
            }

            if (this.currentUser?.userBlacklist?.length > 0) {
                this.userBlacklist = this.currentUser.userBlacklist;
                const blackListResponse = await this.verifyIsUserInBlackList(this.currentUser.email, true);
                if (blackListResponse) {
                    await this.logout();
                }
            }

            return this.currentUser;
        }

        return null;
    }

    async verifyIsUserInBlackList(email: string, skipEvent: boolean) {
        this.userBlacklist = skipEvent ? this.userBlacklist : await this.blacklistService.getBlacklistByUserEmail(email);
        let message = 'BLlvl4';
        for (const row of this.userBlacklist) {
            if (row.level > 2) {
                this.closeLivechat();
            }
            if (row.level === 4) {
                message += row.category === 1 ? 'E' : row.category === 2 ? 'P' : row.category === 3 ? 'IP' : row.category === 4 ? 'C' : 'R';
                notifications.toast(`There was an unexpected error. Please contact customer support at support@divicasales.com. Ref: ${message}.`).show();
                return true;
            }
        }
        return false;
    }

    closeLivechat() {
        this.canOpenLiveChat = false;
        if (window.Intercom) {
            window.Intercom('shutdown');
        }
    }

    async logout() {
        try {
            await this.apiService.doDelete('Logout');
            this.eventAggregator.publish('untrack-user', { userId: this.currentUser?.id });
            this.saveBlogsCurrentPageView(1);
        } finally {
            this.tokenService.destroyToken();
            this.currentUser = null;
            this.userCart = null;
            this.isAuthenticated = false;
            this.eventAggregator.publish('user-updated', { forDelete: true });
            await this.getCart();
            await this.destroyCart();
        }
    }

    clearSession() {
        this.tokenService.destroyToken();
        this.currentUser = null;
        this.userCart = null;
        this.isAuthenticated = false;
        this.eventAggregator.publish('user-updated', { forDelete: true });
    }

    saveAdminView(bool: boolean) {
        window.localStorage[ADMIN_VIEW] = bool;
        this.eventAggregator.publish('admin-view-updated', { bool: bool });
    }

    getAdminView() {
        return (window.localStorage[ADMIN_VIEW] === 'true');
    }

    checkRolesForPanelAccess(user: User) {
        return !!user?.roles?.find(x => x.toLowerCase() === 'viewadminpanel');
    }

    async validateAuthorizationCode(authorizationCode: string, clientId: string, validationCode?: string | null) {
        return await this.apiService.doPost<ILoginResponse>('AuthorizeCode', { authorizationCode, clientId, validationCode });
    }

    saveReferrerCode(userId: string) {
        if (userId) {
            localStorage.setItem(REFERRER_USER_ID, userId);
        } else {
            localStorage.removeItem(REFERRER_USER_ID);
        }
    }

    isLoggedIn() {
        return Boolean(this.currentUser);
    }

    saveBlogsCurrentPageView(page: number) {
        window.localStorage[CURRENT_BLOGS_PAGE_VIEW] = page;
    }

    async getCart() {
        if (this.currentUser) {
            if (this.userCart) {
                this.userCart.forEach((e, index) => {
                    this.userCart[index].tempQuantity = e.quantity;
                    this.userCart[index].quantity = new Intl.NumberFormat('en-US').format(Number(e.quantity));
                    this.userCart[index].selectedQuantity = e.quantity;
                });
                return this.userCart;
            }
            this.userCart = await this.customerService.getUserCartProducts();
            this.userCart.forEach((e, index) => {
                this.userCart[index].cartItemId = index + 1;
                this.userCart[index].tempQuantity = e.quantity;
                this.userCart[index].quantity = new Intl.NumberFormat('en-US').format(Number(e.quantity));
                this.userCart[index].selectedQuantity = e.quantity;
            });
            return this.userCart;
        }
        const stringCart = window.localStorage[CART_KEY];
        if (stringCart && stringCart !== '[]' && stringCart !== '') {
            const cartItems = JSON.parse(stringCart);
            cartItems.forEach((e, index) => {
                cartItems[index].tempQuantity = e.quantity;
            });
            return cartItems;
        }
        this.destroyCart();


    }

    async getCartCount() {
        if (this.currentUser) {
            return await this.customerService.getUserCartProductsCount();
        }
        const stringCart = window.localStorage[CART_KEY];
        if (stringCart && stringCart !== '[]' && stringCart !== '') {
            const cartItems = JSON.parse(stringCart);
            return cartItems.length;
        }
        return 0;
    }

    async destroyCart() {
        if (this.currentUser) {
            await this.customerService.destroyUserCartProducts();
        }
        window.localStorage.removeItem(CART_KEY);
        this.userCart = null;
        this.eventAggregator.publish('cart-updated', { cart: [] });
    }

    getWebsiteShortcode(): string {
        return window.localStorage[WEBSITE_SHORTCODE];
    }

    setWebsiteShortcode(websiteShortcode: string) {
        window.localStorage[WEBSITE_SHORTCODE] = websiteShortcode;
    }

    getClientId() {
        if (baseUrl()!.includes('localhost') || !this.getWebsiteShortcode()) {
            return clientId()!;
        }

        return websiteId.get(this.getWebsiteShortcode())!;
    }

    destroyWebsiteShortcode() {
        window.localStorage.removeItem(WEBSITE_SHORTCODE);
    }

    async getCurrencyOptions() {
        const currencies = await this.CurrencyService.getActiveCurrenciesByWebsite(websiteShortCode());
        this.CURRENCY_OPTIONS = [];
        if (currencies?.length) {
            for (const currency of currencies) {
                this.CURRENCY_OPTIONS.push({
                    id: currency.id,
                    value: currency.code,
                    text: currency.code,
                    displayName: currency.description,
                    icon: currency.imagePath,
                    currencySymbol: currency.symbol,
                    isTrending: currency.isTrending
                });
            }
        }
        return this.CURRENCY_OPTIONS.sort((a, b) => a.value?.toLowerCase()?.localeCompare(b.value?.toLowerCase()));
    }

    checkIfUserAgentPrerender = () => navigator.userAgent.toLowerCase().indexOf('prerender') !== -1;

    async savePreviousCurrency(currency: Currency) {
        window.localStorage[PREVIOUS_CURRENCY_KEY] = currency;
    }

    async getPreviousCurrency() {
        return window.localStorage[PREVIOUS_CURRENCY_KEY] || await this.getCurrencyByCountry();
    }

    async saveCurrency(currency: Currency) {
        if (!currency) return;
        await this.CurrencyService.getStoredCurrencyRates(currency);
        window.localStorage[PREVIOUS_CURRENCY_KEY] = window.localStorage[CURRENCY_KEY];
        const oldCurrency = window.localStorage[PREVIOUS_CURRENCY_KEY];
        window.localStorage[CURRENCY_KEY] = currency.code;
        this.eventAggregator.publish('currency-updated', { currency: currency, oldCurrency: oldCurrency });
    }

    saveLanguage(language: Geolocation) {
        window.localStorage[LANGUAGE_KEY] = language;
        this.eventAggregator.publish('language-updated', { language: language });
    }

    async getUserProxy() {
        return this.userProxy;
    }

    // Add a watch handler for when geolocation changes
    @watch('geolocation')
    geolocationChanged(newValue: Geolocation, oldValue: Geolocation) {
        if (newValue && newValue !== oldValue) {
            this.eventAggregator.publish('geolocation-updated', { geolocation: newValue });
            // Resolve any pending promise
            if (this.geolocationPromise) {
                this.geolocationPromise = null;
            }
        }
    }

    async getCountry() {
        if (!this.geolocation && !this.geolocationPromise) {
            this.geolocationPromise = new Promise((resolve) => {
                const subscription = this.eventAggregator.subscribe('geolocation-updated', payload => {
                    subscription.dispose();
                    resolve(payload.geolocation);
                });

                setTimeout(() => {
                    if (this.geolocationPromise) {
                        subscription.dispose();
                        resolve({ countryCode: 'US' }); // Default fallback
                        this.geolocationPromise = null;
                    }
                }, 5000);
            });
        }

        if (!this.geolocation) {
            const result = await this.geolocationPromise;
            if (result && !this.geolocation) {
                this.geolocation = result;
            }
        }

        return this.geolocation;
    }

    async getState() {
        return this.geolocation?.regionName;
    }

    async getCity() {
        return this.geolocation?.city;
    }

    async getCurrency() {
        return await this.getCurrencyByCountry();
    }

    async getCurrencyByCountry() {
        const country = await this.getCountry();
        if (country && country.countryCode) {
            const options = await this.getCurrencyOptions();
            return options.find(x => x.value === countryToCurrency[country.countryCode])?.value ?? 'USD';
        }
        return 'USD';
    }

    async getLanguage() {
        if (!this.languageWebsiteService.languages) await this.languageWebsiteService.getByWebsite();
        if (this.checkIfUserAgentPrerender()) return this.languageWebsiteService.languages?.find(x => x.language.hrefLang.includes('en'));
        let hrefLang = await this.getLanguageByCountry();
        return this.languageWebsiteService.languages?.find(x => x.language.hrefLang.includes(hrefLang ?? 'en'));
    }

    async getLanguageByCountry() {
        const country = await this.getCountry();
        if (!country) return null;

        return countryToLanguage.get(country.countryCode) ?? 'en';
    }
}
